import { LOCALES } from "@uzverse/core";
import type { Locale } from "@uzverse/core";
import { Avatar, Icon, ThemeToggle, useToast } from "@uzverse/ui";
import type { IconName } from "@uzverse/ui";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { storeLocale } from "../i18n/index.js";
import { useAssistant } from "../state/assistant.js";
import { useSession } from "../state/session.js";
import { syncTheme } from "../theme/sync.js";
import { useAppState } from "../state/useAppState.js";
import { usePrefFlag } from "../state/usePref.js";
import { effectiveLayout, useLayout } from "../world/layout.js";
import { useCommonT, useErrorText } from "./common.js";

export function LanguageSwitch() {
  const { t, i18n } = useTranslation();
  const current = i18n.language as Locale;
  return (
    <label className="lang">
      <span className="sr-only">{t("language.switch", { ns: "common" })}</span>
      <select
        value={current}
        onChange={(e) => {
          const next = e.target.value as Locale;
          storeLocale(next);
          void i18n.changeLanguage(next);
        }}
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {t(`language.${l}`, { ns: "common" })}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Brand, language and theme controls on top of the signed-out screens (login, invite). */
export function AuthHeader() {
  const { t } = useTranslation();
  const tc = useCommonT();
  return (
    <div className="auth-brand">
      <span className="brand">{t("app.name", { ns: "common" })}</span>
      <span className="row">
        <LanguageSwitch />
        <ThemeToggle t={tc} />
      </span>
    </div>
  );
}

/** Keeps <html lang> in sync with the UI language (screen readers, hyphenation). */
export function useDocumentLanguage(): void {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
}

/** The assistant's avatar and name in the sidebar; the name edits in place. */
function AssistantCard({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation("web");
  const errorText = useErrorText();
  const toast = useToast();
  const { name, settings, save } = useAssistant();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const shown = name || "…";

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  async function commit(e: FormEvent) {
    e.preventDefault();
    const next = draft.trim();
    if (!next || next === name) {
      setEditing(false);
      return;
    }
    try {
      await save({ name: next });
      setEditing(false);
    } catch (err) {
      toast.show(errorText(err), "error");
    }
  }

  if (editing) {
    return (
      <form className="assistant-card assistant-edit" onSubmit={(e) => void commit(e)}>
        <Avatar name={shown} assistant size={32} />
        <label className="grow">
          <span className="sr-only">{t("shell.assistantName")}</span>
          <input
            ref={input}
            value={draft}
            maxLength={40}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditing(false);
            }}
          />
        </label>
        <button
          type="submit"
          className="btn btn-icon btn-sm btn-primary"
          aria-label={t("shell.save")}
        >
          <Icon name="check" size={16} />
        </button>
      </form>
    );
  }
  return (
    <button
      type="button"
      className="assistant-card"
      disabled={!settings}
      title={t("nav.renameAssistant")}
      aria-label={`${t("nav.renameAssistant")}: ${shown}`}
      onClick={() => {
        setDraft(name);
        setEditing(true);
      }}
    >
      <Avatar name={shown} assistant size={32} />
      {!collapsed && (
        <span className="assistant-meta">
          <strong>{shown}</strong>
          <span className="muted small">
            <span className="dot dot-ok" /> {t("chat.assistant")}
          </span>
        </span>
      )}
    </button>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  badge?: number;
}

function SideLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className="nav-link"
      aria-label={item.label}
      title={collapsed ? item.label : undefined}
    >
      <Icon name={item.icon} />
      <span className="nav-label">{item.label}</span>
      {item.badge ? (
        <span className="badge" aria-label={String(item.badge)}>
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * Signed-in shell: collapsible sidebar (assistant, navigation, account) on tablet and desktop,
 * a bottom tab bar on phones. `/world` renders full-bleed for the 3D scene.
 */
export function Layout() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  const { signOut, user } = useSession();
  const userId = user?.id;
  useEffect(() => {
    if (userId) void syncTheme();
  }, [userId]);
  const { approvals } = useAppState();
  const location = useLocation();
  const [collapsed, setCollapsed] = usePrefFlag("uz.sidebarCollapsed", false);
  const pendingCount = Object.values(approvals).filter((a) => a.status === "pending").length;
  const bleed = location.pathname.startsWith("/world");
  // Game layout: no sidebar, the world page brings its own menu drawer.
  const layout = effectiveLayout(useLayout());
  const immersive = bleed && layout === "immersive";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed(!collapsed);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [collapsed, setCollapsed]);

  const primary: NavItem[] = [
    { to: "/", label: t("nav.assistant"), icon: "chat", end: true },
    { to: "/world", label: t("nav.world"), icon: "globe" },
    { to: "/companies", label: t("nav.companies"), icon: "building" },
    { to: "/settings", label: t("nav.settings"), icon: "settings" },
  ];
  const more: NavItem[] = [
    { to: "/tasks", label: t("nav.tasks"), icon: "tasks" },
    { to: "/approvals", label: t("nav.approvals"), icon: "shield", badge: pendingCount },
    { to: "/memory", label: t("nav.memory"), icon: "brain" },
    { to: "/credits", label: t("nav.credits"), icon: "cube" },
  ];

  return (
    <div
      className={`app${collapsed ? " is-collapsed" : ""}${bleed ? " is-bleed" : ""}${immersive ? " is-immersive" : ""}`}
    >
      <a className="skip" href="#main">
        {t("skipToContent")}
      </a>
      <aside className="sidebar">
        <div className="sidebar-top">
          <span className="brand">
            <span className="nav-label">{t("app.name", { ns: "common" })}</span>
          </span>
          <button
            type="button"
            className="btn btn-quiet btn-icon collapse-btn"
            aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
            title={`${collapsed ? t("nav.expand") : t("nav.collapse")} (Ctrl+B)`}
            onClick={() => setCollapsed(!collapsed)}
          >
            <Icon name="panelLeft" />
          </button>
        </div>
        <AssistantCard collapsed={collapsed} />
        <nav className="nav" aria-label={t("nav.label")}>
          {primary.map((item) => (
            <SideLink key={item.to} item={item} collapsed={collapsed} />
          ))}
          <span className="nav-group eyebrow nav-label">{t("nav.more")}</span>
          <span className="nav-sep" aria-hidden="true" />
          {more.map((item) => (
            <SideLink key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="user-chip" title={t("shell.signedInAs", { name: user?.displayName })}>
            <Avatar name={user?.displayName ?? "?"} size={28} />
            <span className="nav-label user-name">{user?.displayName}</span>
          </div>
          <div className="foot-controls">
            <LanguageSwitch />
            <ThemeToggle t={tc} />
            <button
              type="button"
              className="btn btn-quiet btn-icon"
              aria-label={t("nav.signOut")}
              title={t("nav.signOut")}
              onClick={() => void signOut()}
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
      </aside>
      <main id="main" className="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
